import crypto from "node:crypto";
import path from "node:path";
import type { CommonAction, LoggerImplement } from "@iflyrpa/share";
import { downloadImage, getFilenameFromUrl, response, TaskState } from "@iflyrpa/share";
import { AxiosProxyParams } from "../../types";
import { stringifyError } from "../../utils";
import { classifyPublishError } from "../../utils/errorMessages";
import { Http } from "../../utils/http";
import { reportLogger } from "../../utils/reportLogger";
import { getShipinhaoUploadAuth, type ShipinhaoUploadAuth } from "../../utils/shipinhao/auth";
import { extractEncFileKey, rid } from "../../utils/shipinhao/utils";
import type { ShipinhaoPublishVideoParams } from "./index";
import { buildEvent, buildExtReading, buildLocation, buildMentionedUser, buildTagInfo, buildTopic } from "./payload";
import { type UploadResult, uploadFile } from "./uploader";
import { buildVideoMetaFromParams, type VideoMeta } from "./videoMeta";
import { validateShipinhaoTitle, validateShipinhaoVideo } from "./videoValidator";

/** 发布相关接口的来源页，headers.referer 与 query._pageUrl 均取此值 */
const POST_CREATE_PAGE_URL = "https://channels.weixin.qq.com/micro/content/post/create";

/** 微前端接口前缀（发布链路全部走 /micro/content 下的网关） */
const MICRO_CONTENT_BASE = "https://channels.weixin.qq.com/micro/content/cgi-bin/mmfinderassistant-bin";

/** 从 extraParam 里取出的浏览器侧标识，缺失时降级为空串（服务端非强校验） */
interface ShipinhaoClientContext {
    /** 会话内固定的 UUID，query._aid */
    aId: string;
    /** 设备指纹，header finger-print-device-id */
    fingerPrintDeviceId: string;
    /** 微信数字 uin，header x-wechat-uin */
    uin: string;
}

function resolveClientContext(params: ShipinhaoPublishVideoParams, fallbackUin: number): ShipinhaoClientContext {
    const extra = (params.extraParam || {}) as Record<string, unknown>;
    const deviceIdCookie = params.cookies.find(
        (c) => c.name === "device_id" || c.name === "finger_print_device_id",
    )?.value;

    return {
        aId: typeof extra.aId === "string" ? extra.aId : "",
        fingerPrintDeviceId:
            typeof extra.fingerPrintDeviceId === "string" ? extra.fingerPrintDeviceId : deviceIdCookie || "",
        uin: typeof extra.uin === "string" ? extra.uin : String(fallbackUin),
    };
}

/** 发布链路公共请求头（对齐真实浏览器请求） */
function buildPublishHeaders(cookieString: string, client: ShipinhaoClientContext): Record<string, string> {
    const headers: Record<string, string> = {
        cookie: cookieString,
        referer: POST_CREATE_PAGE_URL,
        origin: "https://channels.weixin.qq.com",
        "content-type": "application/json",
    };

    if (client.fingerPrintDeviceId) headers["finger-print-device-id"] = client.fingerPrintDeviceId;
    if (client.uin) headers["x-wechat-uin"] = client.uin;

    return headers;
}

/** 发布链路公共 query：_aid 会话固定、_rid 每请求随机、_pageUrl 来源页 */
function buildPublishQuery(client: ShipinhaoClientContext): Record<string, string> {
    const query: Record<string, string> = {
        _rid: rid(),
        _pageUrl: POST_CREATE_PAGE_URL,
    };

    if (client.aId) query._aid = client.aId;

    return query;
}

/** 所有 mmfinderassistant 接口共用的 body 尾部字段 */
function buildCommonBody(finderUsername: string): Record<string, unknown> {
    return {
        timestamp: String(Date.now()),
        _log_finder_uin: "",
        _log_finder_id: finderUsername,
        rawKeyBuff: "",
        pluginSessionId: null,
        scene: 7,
        reqScene: 7,
    };
}

/**
 * 视频号视频发布 - Mock API 方式
 * 通过调用视频号助手平台的内部API实现视频发布
 *
 * 核心流程:
 * 1. helper_upload_params 获取认证 + filetype
 * 2. get-finder-post-trace-key 获取 traceKey
 * 3. 上传视频（applyupload → uploadpart×N → completepart）
 * 4. 上传封面（同 3，filetype=20304）：横屏封面必传，竖屏封面按需再传一次
 * 5. post_clip_video 提交转码
 * 6. post_clip_video_result 轮询至 flag=1
 * 7. post_create 发布
 */

interface TranscodeResult {
    clipKey: string;
    url: string;
    width: number;
    height: number;
    duration: number;
    md5: string;
    fileSize: string;
}

export type PublishVideoAction = CommonAction<ShipinhaoPublishVideoParams, string>;

/** 获取 traceKey（发布体必需） */
async function getTraceKey(
    auth: ShipinhaoUploadAuth,
    client: ShipinhaoClientContext,
    http: Http,
    logger: LoggerImplement,
): Promise<string> {
    const res = await http.api<{ errCode: number; data?: { traceKey: string } }>({
        method: "POST",
        url: `${MICRO_CONTENT_BASE}/post/get-finder-post-trace-key`,
        params: buildPublishQuery(client),
        data: {
            objectId: "",
            ...buildCommonBody(auth.finderUsername),
        },
        defaultErrorMsg: "获取 traceKey 失败",
    });

    if (res.errCode !== 0 || !res.data?.traceKey) {
        logger.error("[getTraceKey] 获取失败:", JSON.stringify(res));
        throw new Error(`获取 traceKey 失败: ${JSON.stringify(res)}`);
    }

    return res.data.traceKey;
}

/**
 * 获取内容声明标注的 tagKey
 *
 * tagKey 由服务端每次下发且带时效（实测形如 `6bd322b86292bee21787555047396788CB7BA97823489`，
 * 中间嵌了 10 位时间戳），不能硬编码或复用。仅在需要声明标注时调用。
 * 拿不到 tagKey 时返回 null，由调用方决定是否降级为不带 tagInfo 发布。
 */
async function getObjectTagKey(
    auth: ShipinhaoUploadAuth,
    client: ShipinhaoClientContext,
    http: Http,
    logger: LoggerImplement,
): Promise<string | null> {
    try {
        const res = await http.api<{
            errCode: number;
            data?: { tagKey?: string; tags?: Array<{ tagType: number; tagName: string }> };
        }>({
            method: "POST",
            url: `${MICRO_CONTENT_BASE}/post/finder_get_object_tag_list`,
            params: buildPublishQuery(client),
            data: {
                source: 1,
                ...buildCommonBody(auth.finderUsername),
            },
            defaultErrorMsg: "获取内容声明标注失败",
        });

        if (res.errCode !== 0 || !res.data?.tagKey) {
            logger.warn(`[getObjectTagKey] 未取到 tagKey: ${JSON.stringify(res)}`);
            return null;
        }

        return res.data.tagKey;
    } catch (error) {
        logger.warn(`[getObjectTagKey] 获取 tagKey 异常: ${stringifyError(error)}`);
        return null;
    }
}

/** 提交转码并轮询至完成 */
async function submitAndPollTranscode(opts: {
    videoUrl: string;
    videoMeta: VideoMeta;
    traceKey: string;
    uploadStartTime: number;
    uploadEndTime: number;
    finderUsername: string;
    client: ShipinhaoClientContext;
    http: Http;
    logger: LoggerImplement;
}): Promise<TranscodeResult> {
    const { videoUrl, videoMeta, traceKey, uploadStartTime, uploadEndTime, finderUsername, client, http, logger } =
        opts;

    // 域名替换：wxapp.tc.qq.com → finder.video.qq.com（实测前端行为）
    const finderUrl = videoUrl.includes("wxapp.tc.qq.com")
        ? `https://finder.video.qq.com${videoUrl.split("qq.com")[1]}`
        : videoUrl;

    logger.info(
        `[submitAndPollTranscode] 视频尺寸: ${videoMeta.width}x${videoMeta.height}, 时长: ${videoMeta.duration}s`,
    );

    const submitRes = await http.api<{ errCode: number; data?: { clipKey: string; draftId: string } }>({
        method: "POST",
        url: `${MICRO_CONTENT_BASE}/post/post_clip_video`,
        params: buildPublishQuery(client),
        data: {
            url: finderUrl,
            timeStart: 0,
            cropDuration: 0,
            height: videoMeta.height,
            width: videoMeta.width,
            x: 0,
            y: 0,
            clipOriginVideoInfo: {
                width: videoMeta.width,
                height: videoMeta.height,
                duration: videoMeta.duration,
                fileSize: videoMeta.fileSize,
            },
            traceInfo: {
                traceKey,
                uploadCdnStart: uploadStartTime,
                uploadCdnEnd: uploadEndTime,
            },
            targetWidth: videoMeta.width,
            targetHeight: videoMeta.height,
            type: 4,
            useAstraThumbCover: 1,
            ...buildCommonBody(finderUsername),
        },
        defaultErrorMsg: "提交转码失败",
    });

    if (submitRes.errCode !== 0 || !submitRes.data?.clipKey) {
        logger.error("[submitAndPollTranscode] 提交转码失败:", JSON.stringify(submitRes));
        throw new Error(`提交转码失败: ${JSON.stringify(submitRes)}`);
    }

    const { clipKey, draftId } = submitRes.data;

    // 轮询转码结果：转码耗时与视频时长正相关，固定 5min 会误杀长视频。
    // 按「视频时长 × 1.5 + 5min 基线」估算上限，并封顶 30min（平台侧再慢就是异常了）。
    const pollInterval = 5000;
    const pollBudget = Math.min(30 * 60 * 1000, 5 * 60 * 1000 + Math.ceil(videoMeta.duration * 1.5) * 1000);
    const maxPolls = Math.ceil(pollBudget / pollInterval);
    let pollCount = 0;

    while (pollCount < maxPolls) {
        await sleep(pollInterval);
        pollCount++;

        const pollRes = await http.api<{
            errCode: number;
            data?: {
                flag: number;
                url?: string;
                width?: number;
                height?: number;
                duration?: number;
                md5?: string;
                fileSize?: string;
            };
        }>(
            {
                method: "POST",
                url: `${MICRO_CONTENT_BASE}/post/post_clip_video_result`,
                params: buildPublishQuery(client),
                data: {
                    clipKey,
                    draftId,
                    ...buildCommonBody(finderUsername),
                },
                defaultErrorMsg: "转码轮询失败",
            },
            { timeout: 60_000, retries: 2, retryDelay: 3000 },
        );

        if (pollRes.errCode !== 0) {
            logger.error(`[submitAndPollTranscode] 转码轮询失败 (poll ${pollCount}):`, JSON.stringify(pollRes));
            throw new Error(`转码轮询失败 (poll ${pollCount}): ${JSON.stringify(pollRes)}`);
        }

        const { flag, url, width, height, duration, md5, fileSize } = pollRes.data || {};

        if (flag === 1) {
            // 转码完成
            if (!url || !width || !height || !duration || !md5 || !fileSize) {
                logger.error("[submitAndPollTranscode] 转码完成但返回数据不完整:", JSON.stringify(pollRes.data));
                throw new Error(`转码完成但返回数据不完整: ${JSON.stringify(pollRes.data)}`);
            }
            logger.info(`[submitAndPollTranscode] 转码完成! 用时: ${(pollCount * pollInterval) / 1000}s`);
            return { clipKey, url, width, height, duration, md5, fileSize };
        } else if (flag === 2) {
            // 处理中
        } else {
            // 未知状态，视为失败
            logger.error(`[submitAndPollTranscode] 转码失败，未知 flag=${flag}:`, JSON.stringify(pollRes.data));
            throw new Error(`转码失败，未知 flag=${flag}: ${JSON.stringify(pollRes.data)}`);
        }
    }

    logger.error(
        `[submitAndPollTranscode] 转码超时，已轮询 ${maxPolls} 次，用时: ${(maxPolls * pollInterval) / 1000}s`,
    );
    throw new Error(`转码超时 (${(maxPolls * pollInterval) / 1000}s)`);
}

/** 发布视频 */
async function publishVideo(opts: {
    params: ShipinhaoPublishVideoParams;
    auth: ShipinhaoUploadAuth;
    client: ShipinhaoClientContext;
    clipResult: TranscodeResult;
    videoUpload: UploadResult;
    /** 横屏封面上传结果 → thumbUrl / fullThumbUrl */
    coverUpload: UploadResult;
    /** 竖屏封面上传结果 → coverUrl / fullCoverUrl / shareCoverUrl */
    verticalCoverUpload: UploadResult;
    videoMeta: VideoMeta;
    traceKey: string;
    /** 内容声明 tagKey，未声明标注时为 null */
    tagKey: string | null;
    uploadStartTime: number;
    uploadEndTime: number;
    proxyHttp: Http;
    logger: LoggerImplement;
}): Promise<{ errCode: number; data?: { baseResp: { errcode: number; errmsg: string } } }> {
    const {
        params,
        auth,
        client,
        clipResult,
        videoUpload,
        coverUpload,
        verticalCoverUpload,
        videoMeta,
        traceKey,
        tagKey,
        uploadStartTime,
        uploadEndTime,
        proxyHttp,
        logger,
    } = opts;

    // 域名替换封面 URL：上传返回 wxapp.tc.qq.com，发布体里要换成 finder.video.qq.com
    const toFinderUrl = (url: string) =>
        url.includes("wxapp.tc.qq.com") ? `https://finder.video.qq.com${url.split("qq.com")[1]}` : url;

    // 横屏封面 → thumbUrl / fullThumbUrl
    const thumbFinderUrl = toFinderUrl(coverUpload.downloadUrl);
    // 竖屏封面 → coverUrl / fullCoverUrl / shareCoverUrl（未传竖屏封面时与横屏同图）
    const coverFinderUrl = toFinderUrl(verticalCoverUpload.downloadUrl);

    // 生成 UUID 作为 md5sum（实测行为，服务端不校验）
    const md5sumUuid = crypto.randomUUID();

    const description = params.description;

    const objectDesc: Record<string, unknown> = {
        mpTitle: "",
        description,
        extReading: buildExtReading(params.link),
        mediaType: 4,
        location: buildLocation(params.location),
        // 注意传原始文案而非拼接后的 description：
        // finderTopicInfo 的 value0 只放正文，话题与 @用户各占独立 value 节点
        topic: buildTopic({
            description: params.description,
            topics: params.topics,
            mentionedUsers: params.mentionedUsers,
            collection: params.collection,
        }),
        event: buildEvent(params.event),
        mentionedUser: buildMentionedUser(params.mentionedUsers),
        media: [
            {
                url: clipResult.url,
                fileSize: clipResult.fileSize,
                // 横屏封面
                thumbUrl: thumbFinderUrl,
                fullThumbUrl: thumbFinderUrl,
                // 竖屏封面（未传 verticalCoverPath 时与横屏同图）
                coverUrl: coverFinderUrl,
                fullCoverUrl: coverFinderUrl,
                shareCoverUrl: coverFinderUrl,
                mediaType: 4,
                videoPlayLen: Math.round(clipResult.duration),
                width: clipResult.width,
                height: clipResult.height,
                // md5sum 实测填 UUID 而非真实 md5，服务端不校验
                md5sum: md5sumUuid,
                cardShowStyle: 2,
                urlCdnTaskId: clipResult.clipKey,
            },
        ],
        // shortTitle 是数组结构，不是字符串（实测）
        shortTitle: params.title ? [{ shortTitle: params.title }] : [],
        member: {},
    };

    const publishData: Record<string, unknown> = {
        objectType: 0,
        // 顶层坐标实测恒为 0，真实坐标只写在 objectDesc.location
        longitude: 0,
        latitude: 0,
        feedLongitude: 0,
        feedLatitude: 0,
        originalFlag: params.originalFlag ?? 0,
        topics: params.topics || [],
        isFullPost: 1,
        handleFlag: 2,
        videoClipTaskId: clipResult.clipKey,
        traceInfo: {
            traceKey,
            uploadCdnStart: uploadStartTime,
            uploadCdnEnd: uploadEndTime,
        },
        objectDesc,
        report: {
            clipKey: clipResult.clipKey,
            draftId: clipResult.clipKey,
            ...buildCommonBody(auth.finderUsername),
            // report 是埋点，带的是转码**前**的原始视频信息
            height: videoMeta.height,
            width: videoMeta.width,
            duration: videoMeta.duration,
            fileSize: videoUpload.fileSize,
            uploadCost: (uploadEndTime - uploadStartTime) * 1000,
        },
        postFlag: 0,
        mode: 1,
        clientid: crypto.randomUUID(),
        ...buildCommonBody(auth.finderUsername),
    };

    if (params.scheduledTime) {
        publishData.effectiveTime = params.scheduledTime;
    }

    // 内容声明标注：调用方传的 tagInfo 原样透传，只补实时 tagKey；缺 tagKey 时整体省略
    if (params.tagInfo && tagKey) {
        publishData.tagInfo = buildTagInfo(params.tagInfo, tagKey);
    }

    const publishRes = await proxyHttp.api<{
        errCode: number;
        data?: { baseResp: { errcode: number; errmsg: string } };
    }>({
        method: "POST",
        url: `${MICRO_CONTENT_BASE}/post/post_create`,
        params: buildPublishQuery(client),
        data: publishData,
        defaultErrorMsg: "发布视频失败",
    });

    logger.info(
        `[publishVideo] 发布结果: errCode=${publishRes.errCode}, baseResp.errcode=${publishRes.data?.baseResp?.errcode}`,
    );
    return publishRes;
}

function sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 把封面路径解析成本地文件路径
 *
 * 第三方传的 coverPath 常常是 OSS 上的 http(s) 地址（见 params.json），
 * 而 uploadFile 走 fs.statSync/openSync 分片读取，只认本地路径，
 * 所以远程地址要先下载到临时目录。本地路径原样返回。
 *
 * @returns 本地文件路径
 */
async function resolveLocalCoverPath(
    coverPath: string,
    label: string,
    tmpCachePath: string,
    logger: LoggerImplement,
): Promise<string> {
    if (!/^https?:\/\//i.test(coverPath)) return coverPath;

    const fileName = getFilenameFromUrl(coverPath);
    const savePath = path.join(tmpCachePath, `${Date.now()}-${label}-${fileName}`);
    await downloadImage(coverPath, savePath);
    return savePath;
}

export const mockAction: PublishVideoAction = async (task, params) => {
    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");

    let currentStep = "初始化";

    try {
        // 1. 转换 cookies 为字符串
        currentStep = "解析认证信息";
        const cookieString = params.cookies.map((c) => `${c.name}=${c.value}`).join("; ");

        // 创建 Http 实例：http 用于认证与 CDN 上传（只需 cookie）
        const http = new Http({ headers: { cookie: cookieString } });

        // 2. 验证参数
        currentStep = "验证发布参数";
        if (!params.videoPath) {
            return response(414, "视频文件路径不能为空", "");
        }
        if (!params.coverPath) {
            return response(414, "横屏封面图片路径不能为空", "");
        }

        // 3. 获取认证 + filetype
        currentStep = "获取上传认证";
        const auth = await getShipinhaoUploadAuth(cookieString, http);

        // 3.1 组装浏览器侧标识（_aid / finger-print-device-id / x-wechat-uin）
        // 这三项来自 extraParam，缺失时退化为 cookie 里的 device_id 与 auth 的 uin。
        const client = resolveClientContext(params, auth.uin);

        const publishHeaders = buildPublishHeaders(cookieString, client);
        // microHttp 走 /micro/content 网关（traceKey/转码/标注），proxyHttp 用于发布并带代理
        const microHttp = new Http({ headers: publishHeaders });
        const args: AxiosProxyParams = [
            { headers: publishHeaders },
            task.logger,
            params.proxyLoc,
            params.accountId,
            "shipinhao",
        ];
        const proxyHttp = new Http(...args);

        // 4. 组装视频元数据：宽高时长用调用方传入值，编码格式从文件分段读取
        currentStep = "组装视频元数据";
        const videoMeta = buildVideoMetaFromParams(params.videoPath, params.videoMetadata);
        task.logger.info(
            `[shipinhaoPublishVideo] 视频: ${videoMeta.width}×${videoMeta.height}, ${videoMeta.duration.toFixed(2)}s, ${(videoMeta.fileSize / 1024 / 1024).toFixed(2)}MB, codec=${videoMeta.codec || "unknown"}`,
        );

        // 4.1 校验平台限制（时长 8 小时 / 大小 20GB / MP4+H.264），上传前拦截
        currentStep = "校验视频限制";
        const validationError = validateShipinhaoVideo(params.videoPath, videoMeta);
        if (validationError) {
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: validationError,
            });
            return response(414, validationError, "");
        }

        // 4.2 校验标题长度
        currentStep = "校验标题";
        const titleError = validateShipinhaoTitle(params.title);
        if (titleError) {
            await updateTaskState?.({
                state: TaskState.FAILED,
                error: titleError,
            });
            return response(414, titleError, "");
        }

        // 5. 获取 traceKey
        currentStep = "获取 traceKey";
        const traceKey = await getTraceKey(auth, client, microHttp, task.logger);

        // 5.1 需要内容声明标注时，取实时 tagKey（带时效，不能缓存复用）
        let tagKey: string | null = null;
        if (params.tagInfo) {
            currentStep = "获取内容声明 tagKey";
            tagKey = await getObjectTagKey(auth, client, microHttp, task.logger);
        }

        const uploadStartTime = Math.floor(Date.now() / 1000);

        // 6. 上传视频
        currentStep = "上传视频";
        const videoUpload = await uploadFile({
            filePath: params.videoPath,
            fileType: auth.videoFileType,
            uin: auth.uin,
            authKey: auth.authKey,
            http,
            logger: task.logger,
            // 视频用默认策略：按文件大小和最低吞吐（32KB/s）反推总预算，单片按 64KB/s 推算超时
        });

        const uploadEndTime = Math.floor(Date.now() / 1000);
        task.logger.info(`[shipinhaoPublishVideo] 耗时: ${uploadEndTime - uploadStartTime}s`);

        // 7. 上传横屏封面（thumbUrl / fullThumbUrl）
        currentStep = "上传横屏封面";
        const localCoverPath = await resolveLocalCoverPath(
            params.coverPath,
            "横屏封面",
            task.getTmpPath(),
            task.logger,
        );
        const coverUpload = await uploadFile({
            filePath: localCoverPath,
            fileType: auth.pictureFileType,
            uin: auth.uin,
            authKey: auth.authKey,
            http,
            logger: task.logger,
            // 封面通常几百 KB，单片 3 分钟超时足够
        });

        // task.logger.info(`coverUpload :  ${JSON.stringify(coverUpload)}`)

        // 7.1 上传竖屏封面（coverUrl / fullCoverUrl / shareCoverUrl）；不传则与横屏同图
        let verticalCoverUpload = coverUpload;
        if (params.verticalCoverPath) {
            currentStep = "上传竖屏封面";
            const localVerticalPath = await resolveLocalCoverPath(
                params.verticalCoverPath,
                "竖屏封面",
                task.getTmpPath(),
                task.logger,
            );
            verticalCoverUpload = await uploadFile({
                filePath: localVerticalPath,
                fileType: auth.pictureFileType,
                uin: auth.uin,
                authKey: auth.authKey,
                http,
                logger: task.logger,
            });
        }

        // task.logger.info(`verticalCoverUpload: ${JSON.stringify(verticalCoverUpload)}`)

        // 8. 提交转码
        currentStep = "提交转码";
        const clipResult = await submitAndPollTranscode({
            videoUrl: videoUpload.downloadUrl,
            videoMeta,
            traceKey,
            uploadStartTime,
            uploadEndTime,
            finderUsername: auth.finderUsername,
            client,
            http: microHttp,
            logger: task.logger,
        });

        // 9. 发布视频
        currentStep = "发布视频";
        let publishResult: Awaited<ReturnType<typeof publishVideo>>;
        try {
            publishResult = await publishVideo({
                params,
                auth,
                client,
                clipResult,
                videoUpload,
                coverUpload,
                verticalCoverUpload,
                videoMeta,
                traceKey,
                tagKey,
                uploadStartTime,
                uploadEndTime,
                proxyHttp,
                logger: task.logger,
            });
        } catch (error) {
            const handledError = Http.handleApiError(error);
            task.logger.error(
                `[shipinhaoPublishVideo] 发布请求失败: ${handledError.message}`,
                stringifyError(handledError),
            );
            const classified = classifyPublishError(handledError);
            if (classified) {
                const message = `${classified.userMessage}${task.debug ? ` ${http.proxyInfo}` : ""}`;
                await updateTaskState?.({
                    state: TaskState.FAILED,
                    error: message,
                });
                return { code: 414, data: "", message };
            }
            throw error;
        }

        task.logger.info(`[shipinhaoPublishVideo] publishResult: ${JSON.stringify(publishResult)}`);

        // 10. 处理发布结果
        const resultCode = publishResult.data?.baseResp?.errcode ?? publishResult.errCode;
        // 服务端只回顶层 errCode（无 baseResp）时不能兜底成“发布成功”，否则失败分支会打出自相矛盾的文案
        const resultMsg =
            publishResult.data?.baseResp?.errmsg ??
            (resultCode === 0 ? "发布成功" : `发布失败（errCode=${resultCode}）`);

        if (resultCode === 0) {
            // 关联 id 取横屏封面的 encfilekey，与图文发布（shipinhaoPublish）保持一致。
            // 不能用 clipKey：它只是转码任务 id，发布完成后在作品列表里查不到对应记录。
            const publishId = extractEncFileKey(verticalCoverUpload.downloadUrl);
            if (!publishId) {
                task.logger.error(
                    `[shipinhaoPublishVideo] 封面 DownloadURL 中未解析到 encfilekey，关联 id 为空: ${verticalCoverUpload.downloadUrl}`,
                );
            }

            await updateTaskState?.({
                state: TaskState.SUCCESS,
                result: {
                    response: resultMsg,
                },
            });

            // 上报日志
            reportLogger({
                token: params.huiwenToken || "",
                enverionment: task.enverionment || "development",
                postId: params.articleId,
                eip: proxyHttp.proxyInfo,
                accountId: params.accountId,
                uid: params.uid,
                publishParams: {
                    videoPath: params.videoPath,
                    coverPath: params.coverPath,
                    verticalCoverPath: params.verticalCoverPath,
                    title: params.title,
                    topics: params.topics,
                    mentionedUsers: params.mentionedUsers,
                    collection: params.collection,
                    event: params.event,
                    link: params.link,
                    tagInfo: params.tagInfo,
                    originalFlag: params.originalFlag,
                    scheduledTime: params.scheduledTime,
                },
                platform: "shipinhao",
            });

            return response(0, "发布成功", publishId);
        }

        // 处理特殊错误码
        let errorMessage = resultMsg;
        if (resultCode === -11224) {
            errorMessage = "视频号管理员完成实名且绑定手机号后才可以发表";
        } else if (resultCode === 300333 || resultCode === 300334) {
            errorMessage = "登录失效";
        } else if (resultCode === 300330) {
            errorMessage = "未登录";
        } else if (resultCode === 300002) {
            errorMessage = "官方平台在校验音乐/位置/定时信息时失败了，请重新编辑后发布";
        }

        task.logger.error(`[shipinhaoPublishVideo] 发布失败: ${errorMessage} (errCode=${resultCode})`);
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: errorMessage,
        });
        return response(414, errorMessage, "");
    } catch (error) {
        const handledError = Http.handleApiError(error);
        const errorMsg = handledError.message || "发布失败，请稍后重试";

        task.logger.error(
            `[shipinhaoPublishVideo] 发布流程异常 [${currentStep}]: ${errorMsg}`,
            stringifyError(error),
            handledError.extra,
        );

        await updateTaskState?.({
            state: TaskState.FAILED,
            error: errorMsg,
        });

        return response(414, errorMsg, "");
    }
};
